import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import propTypes from 'prop-types';
import PhotoLightbox from '../photo-lightbox/photo-lightbox';
import mergeI18n from '../../lib/util/merge-i18n';
import i18n from './i18n';
import PhotoTabs from '../photo-tabs/photo-tabs';
import newWindowIcon from './ic_open_in_new_black_24px.svg';
import { ERROR_ROUTE, LOG_IN_ROUTE } from '../../constants/constants';
import { getPhotos } from '../../services/request-processor';
import Spinner from '../spinner/spinner';

// The photos the lightbox shows, and the one it opens on, for a click in the given photo set
export function lightboxView (photoSets, photoSetId, photoIndex, mainPhoto) {
	// The main photo is one of the standard photos, so it opens within that set
	const setId = photoSetId === 'main' ? 'standard' : photoSetId;
	const photos = _.get(_.find(photoSets, { id: setId }), 'photos', []);
	const index = photoSetId === 'main' ? Math.max(photos.indexOf(mainPhoto), 0) : photoIndex;
	return { photos, index };
}

class Condition extends React.Component {
	constructor (props) {
		super(props);

		this.state = {
			isLoading: true,
			photoSets: [],
			vehicle: {},
			mainPhoto: null,
			currentPhotoTabId: 'standard',
			isLightboxOpen: false,
			lightboxPhotos: [],
			lightboxIndex: 0
		};

		this.onTabClick = this.onTabClick.bind(this);
		this.onPhotoClick = this.onPhotoClick.bind(this);
		this.onLightboxClose = this.onLightboxClose.bind(this);
	}

	// Load all of the data
	componentDidMount () {
		if (_.isEmpty(this.props.inspectionRequest)) {
			this.props.setRoute(LOG_IN_ROUTE);
			return;
		} else {
			this.props.setParentState({
				progressIndex: -1,
				canLogOut: true,
				canGoToHomePage: false
			});
		}

		const tokenClaims = JSON.parse(window.sessionStorage.getItem('tokenClaims'));
		const vehicle = this.props.inspectionRequest.vehicles.find(item => {
			return item.id === tokenClaims.vehicleId;
		});
		this.setState({ vehicle });

		const token = window.sessionStorage.getItem('token');
		getPhotos(vehicle.id, token, (err, res) => {
			const photos = _.get(res, 'body.results');

			if (err || !photos) {
				// TODO: newrelic notice error here
				this.setState({ isLoading: false });
				this.props.setRoute(ERROR_ROUTE);
			} else {
				let standardPhotos = [];
				let damagePhotos = [];
				photos.forEach(photo => {
					if (photo.type === 'damage') {
						if (photo.damageDisplay) {
							let display = photo.damageDisplay;
							if ('area' in display && 'type' in display && 'severity' in display) {
								photo.description = `${display.area} - ${display.type} ${display.severity}`;
							}
						}
						damagePhotos.push(photo);
					} else {
						standardPhotos.push(photo);
					}
				});

				let mainPhoto = standardPhotos.find(photo => {
					return photo.location === 'lfc';
				});
				if (!mainPhoto) {
					mainPhoto = standardPhotos[0];
				}

				this.setState({
					isLoading: false,
					photoSets: [
						{
							id: 'standard',
							photos: standardPhotos
						},
						{
							id: 'damage',
							photos: damagePhotos
						}
					],
					mainPhoto
				});
			}
		});
	}

	onTabClick (e, photoTabId) {
		e.preventDefault();
		this.setState({ currentPhotoTabId: photoTabId });
	}

	onPhotoClick (e, photoSetId, photoIndex) {
		e.preventDefault();
		this.setState((previousState) => {
			const { photos, index } = lightboxView(previousState.photoSets, photoSetId, photoIndex, previousState.mainPhoto);
			if (!photos.length) {
				return null;
			}

			return {
				isLightboxOpen: true,
				lightboxPhotos: photos,
				lightboxIndex: index
			};
		});
	}

	onLightboxClose () {
		this.setState({ isLightboxOpen: false });
	}

	render () {
		// Dispite changing the route in componentDidMount React will attempt to render this
		// at least once, so we're doing this to avoid a crash
		if (_.isEmpty(this.props.inspectionRequest)) {
			return (<div />);
		}

		if (this.state.isLoading) {
			return (<Spinner />);
		}

		const __ = mergeI18n(this.props.language, i18n);

		const greeting = __.welcomeMessage + (this.props.inspectionRequest.lesseeFirstName ? `, ${this.props.inspectionRequest.lesseeFirstName}!` : '!');
		const inspectionDateStr = moment(this.state.vehicle.completedAt, moment.ISO_8601).format('MMMM D, YYYY');
		const token = window.sessionStorage.getItem('token');
		const crUrl = `${import.meta.env.VITE_REQUEST_PROCESSOR_URL}/api/v1/inspection-request/vehicle/${this.state.vehicle.id}/condition-report?token=${token}`;

		document.title = `${__.titleStem} - ${__.pageTitle}`;
		return (
			<div id='lss-content' className='container'>
				<div className='row'>
					<div className='col-xs-12'>
						<div id='condition'>
							<PhotoLightbox
								open={this.state.isLightboxOpen}
								photos={this.state.lightboxPhotos}
								index={this.state.lightboxIndex}
								onClose={this.onLightboxClose}
							/>

							<div className='row'>
								<div className='col-xs-12'>
									<h1>{greeting}</h1>
								</div>

							</div>
							<div className='header-bar'>
								<div className='header-text'>{__.overview}</div>
							</div>

							<div className='row'>
								<div className='col-xs-12'>
									{this.state.mainPhoto &&
										<a href='#mainphoto' onClick={(e) => this.onPhotoClick(e, 'main', 0)}>
											<img className='main-photo' src={this.state.mainPhoto.url} alt={this.state.mainPhoto.description} />
										</a>}
									<div className='inspection-info'>
										<h2>{this.state.vehicle.year} {this.state.vehicle.make} {this.state.vehicle.model}</h2>
										<div className='vin'>VIN: {this.state.vehicle.vin}</div>
										<div className='inspection-fact'>{__.inspectionDate}&nbsp; {inspectionDateStr}</div>
										<div className='inspection-fact'>
											{__.status}
											<span className='green-oval'>&#9679;</span>
											{__.completed}
										</div>
										<a className='btn btn-default' href={crUrl} target='_blank' rel='noopener noreferrer'>
											View CR <img className='new-window-icon' src={newWindowIcon} alt='' />
										</a>
									</div>
								</div>
							</div>

							<PhotoTabs
								language={this.props.language}
								photoSets={this.state.photoSets}
								currentPhotoTabId={this.state.currentPhotoTabId}
								onTabClick={this.onTabClick}
								onPhotoClick={this.onPhotoClick}
							/>
						</div>
					</div>
				</div>
			</div>
		);
	}
}

Condition.propTypes = {
	inspectionRequest: propTypes.shape({
		confirmationNo: propTypes.number,
		clickTaskType: propTypes.string,
		lesseeFirstName: propTypes.string,
		lesseeLastName: propTypes.string,
		lesseeEmail: propTypes.string,
		vehicles: propTypes.arrayOf(propTypes.shape({
			vin: propTypes.string.isRequired,
			make: propTypes.string,
			model: propTypes.string,
			year: propTypes.string
		}))
	}).isRequired,
	language: propTypes.string.isRequired,
	setParentState: propTypes.func.isRequired,
	setRoute: propTypes.func.isRequired
};
export default Condition;
